import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { faRedo } from '@fortawesome/pro-regular-svg-icons'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { buildQueryParams } from '@/utils/routeHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIsEstimateWithinActiveQuarter,
  selectStatusForTaxEstimate,
  selectUserTaxEstimateById,
} from '../../userTaxEstimates.selector'
import { Button } from '@/ui'
import { EstimateStatus } from '../../service'
import { UserTaxEstimate } from '../../userTaxEstimates.slice'
import {
  BreakdownVersion,
  selectCalculatorVersionByTaxEstimateId,
  selectIsMultiStateCalculationWithoutEngineOutputByTaxEstimateId,
} from '@/selectors/userTaxCalculation.selectors'
import useTaxPaymentInfoFlyout from '../TaxPaymentInfoFlyout'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { UpdateQuarterlyTaxEstimateDialog } from '../UpdateQuarterlyTaxEstimateDialog'

export const createLinkToTaxBreakdown = (
  estimate: UserTaxEstimate | null,
  queryParams?: Parameters<typeof buildQueryParams>[0]
) => {
  const isFederal = estimate?.type === 'federal'
  const params = {
    ...(!isFederal && estimate?.filingState
      ? { state: estimate.filingState }
      : {}),
    ...queryParams,
  }
  const serializedParams = buildQueryParams(params)

  return `/taxes/quarterly/${estimate?.taxQuarter}/estimate-breakdown?section=${isFederal ? 'federal' : 'state'}${serializedParams ? `&${serializedParams}` : ''}`
}

export const TaxEstimateCta = ({ estimateId }: { estimateId?: number }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  const status = useReselector(selectStatusForTaxEstimate, estimateId)
  const hasBreakdownPage =
    useReselector(selectCalculatorVersionByTaxEstimateId, estimateId) !==
    BreakdownVersion.defaultBreakdown
  const isMultiStateWithoutEngineOutput = useReselector(
    selectIsMultiStateCalculationWithoutEngineOutputByTaxEstimateId,
    estimateId
  )
  const isWithinActiveQuarter = useReselector(
    selectIsEstimateWithinActiveQuarter,
    estimateId
  )
  const opsRecalcEntryEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.qteOpsRecalcEntry,
    false
  )
  const [modalOpen, setModalOpen] = useState(false)

  const openFlyout = useTaxPaymentInfoFlyout({
    estimateId,
    openQTEModal: () => setModalOpen(true),
  })

  useEffect(() => {
    dispatch(fetchUserDocuments())
  }, [dispatch])

  const isFederal = estimate?.type === 'federal'

  // `recalculateCurrentQuarter` takes no quarter argument, so this must never
  // render on a row for a quarter other than the active one. The flag scopes
  // this to the accounts ops worked out by hand before the multi-state engine
  // shipped: a multi-state calc missing engine output is also the shape of one
  // still waiting on the engine, so the data alone does not isolate the cohort.
  const showRecalculationCta =
    opsRecalcEntryEnabled &&
    isFederal &&
    isMultiStateWithoutEngineOutput &&
    isWithinActiveQuarter &&
    (status === EstimateStatus.readyForPayment ||
      status === EstimateStatus.estimateIsZero)

  return (
    <div className="w-full flex flex-wrap items-center gap-4 mt-2">
      {(status === EstimateStatus.upcoming ||
        status === EstimateStatus.paid ||
        status === EstimateStatus.joinedTooLate ||
        status === EstimateStatus.unpaidWithResponse) && (
        <div className="w-full flex justify-start">
          <Button onClick={() => setModalOpen(true)} variant="secondary">
            Update Payment
          </Button>
        </div>
      )}

      {(status === EstimateStatus.readyForPayment ||
        status === EstimateStatus.estimateIsZero) &&
        (isFederal && hasBreakdownPage ? (
          <div className="w-full sm:w-1/2">
            <Button
              onClick={() => navigate(createLinkToTaxBreakdown(estimate))}
            >
              {status === EstimateStatus.estimateIsZero
                ? 'View Details'
                : 'View Details & Pay'}
            </Button>
          </div>
        ) : (
          <>
            <Button className="w-full sm:w-1/4" onClick={openFlyout}>
              View & Pay
            </Button>
            <Button
              className="w-full sm:w-1/4"
              onClick={() => setModalOpen(true)}
              variant="secondary"
            >
              Mark as Paid
            </Button>
          </>
        ))}

      {showRecalculationCta && (
        <Button
          className="w-full sm:w-auto"
          variant="secondary"
          iconLeft={faRedo}
          onClick={() =>
            navigate('/taxes/quarterly/recalculate', {
              state: { returnTo: '/taxes/quarterly' },
            })
          }
        >
          Make Adjustments
        </Button>
      )}

      {status === EstimateStatus.unpaidWithoutResponse && (
        <>
          <div className="hidden sm:block sm:w-3/4" />
          <Button
            className="w-full sm:w-1/4 flex"
            onClick={() => setModalOpen(true)}
          >
            Update Payment
          </Button>
        </>
      )}

      {estimate && (
        <UpdateQuarterlyTaxEstimateDialog
          open={modalOpen}
          close={() => setModalOpen(false)}
          estimate={estimate}
        />
      )}
    </div>
  )
}
